import { useState } from "react";
import { updateUser } from "../services/userService";
import { getErrorMessage } from "../utils/api/errorUtils";

type UserRoleLabel =
  | "Admin"
  | "Inspector"
  | "Owner"
  | "Lender"
  | "GC"
  | "Architect";
type UserStatusLabel = "Active" | "Pending" | "Inactive";
type UserRoleApi =
  | "admin"
  | "inspector"
  | "owner"
  | "lender"
  | "gc"
  | "architect";
type UserStatusApi = "active" | "pending_invite" | "inactive";

const ROLE_API_MAP: Record<UserRoleLabel, UserRoleApi> = {
  Admin: "admin",
  Inspector: "inspector",
  Owner: "owner",
  Lender: "lender",
  GC: "gc",
  Architect: "architect",
};

const STATUS_API_MAP: Record<UserStatusLabel, UserStatusApi> = {
  Active: "active",
  Pending: "pending_invite",
  Inactive: "inactive",
};

export interface UpdateUserFormState {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  roles: UserRoleLabel[];
  status: UserStatusLabel;
  phone: string;
  address?: string;
  bio: string;
  branchId?: string;
  designation?: string;
  focused?: string[];
  projects?: string[];
}

interface UpdateUserPayload {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  roles?: UserRoleApi[];
  status: UserStatusApi;
  lenderId: string | null;
  phone?: string;
  address?: string;
  bio?: string;
  designation?: string;
  focused?: string[];
  projectIds?: string[];
}

type UpdateUserResult = { success: true } | { success: false; error: string };

interface ApiError {
  message?: string;
}

const useUpdateUser = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleUpdateUser = async (
    id: string,
    formState: UpdateUserFormState,
  ): Promise<UpdateUserResult> => {
    setLoading(true);
    setError("");

    const payload: UpdateUserPayload = {
      firstName: formState.firstName.trim(),
      lastName: formState.lastName.trim(),
      email: formState.email.trim(),
      companyName: formState.company.trim(),
      status: STATUS_API_MAP[formState.status] ?? "active",
      lenderId: formState.branchId || null,
    };
    // PhoneInput emits only the dial code (e.g. "+1") when no number is entered.
    // Optional fields are always sent (empty string when cleared) so removing a value saves.
    const phone = formState.phone.trim();
    payload.phone = phone.replace(/\D/g, "").length > 3 ? phone : "";
    payload.address = formState.address?.trim() ?? "";
    payload.bio = formState.bio.trim();
    if (formState.roles.length) payload.roles = formState.roles.map((r) => ROLE_API_MAP[r]);
    if (formState.roles.includes("Inspector")) {
      if (formState.designation) payload.designation = formState.designation;
      if (formState.focused?.length) payload.focused = formState.focused;
    }
    if (formState.roles.every((r) => r === "Owner" || r === "Architect")) {
      payload.projectIds = (formState.projects ?? []).map(String);
    }

    const { error: apiError } = (await updateUser(id, payload)) as {
      error: ApiError | null;
    };

    setLoading(false);

    if (apiError) {
      const msg = getErrorMessage(apiError, "Failed to update user.");
      setError(msg);
      return { success: false, error: msg };
    }

    return { success: true };
  };

  return { handleUpdateUser, loading, error };
};

export default useUpdateUser;
